import {clamp} from 'lodash';
import {useMemo} from 'react';

import {useRunSetsQuery} from '../../state/runs/hooks';
import {type Grouping} from '../../state/views/groupSelections/types';
import {runColor} from '../../util/colors';
import {
  type ClassLabelMap,
  classLabelMap,
  getMediaMetadata,
} from '../../util/media';
import {
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_MASK_CLASS_LABEL_KEY,
} from '../../util/mediaConstants';
import {type RunWithHistoryAndMediaWandb} from '../MediaCard';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode/SingleRunModeContext';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  type Axis,
  type LayoutMode,
  type MediaBrowserPanelConfig,
  rowsWithKeysMemo,
} from './common';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from './Component';
import * as PANEL_MEDIA_BROWSER_CONFIG from './config';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import {useStepMeta} from './hooks/useStepMeta';
import {getCompareConfigDefaults} from './layout/utils/getCompareConfigDefaults';
import {type StepMeta} from './types';
import {resolveCurrentStep} from './utils/stepUtils';

export function defaultColumnCount(
  mediaCount?: number,
  mode?: LayoutMode
): number {
  if (mode === 'compare') {
    return 2;
  }
  if (mediaCount != null && mediaCount < 4) {
    return mediaCount;
  }
  return PANEL_MEDIA_BROWSER_CONFIG.DEFAULT_COLUMN_COUNT;
}

/**
 * Generates config with smart defaults for all layout modes.
 *
 * For compare mode, this also generates smart column and tile control defaults
 * based on available runtime data (which dimension has the most variation).
 *
 * @param config - Raw user config
 * @param mediaSliderKey - Key to use for step slider
 * @param isSingleRun - Whether viewing a single run
 * @param mediaCount - Count of media items
 * @param layoutMode - Current layout mode (gallery/grid/compare)
 * @param runsWithMedia - Optional: Runs with media data (for compare mode defaults)
 * @param stepMeta - Optional: Step metadata (for compare mode defaults)
 * @returns Config with all defaults applied
 */
export function getConfigWithDefaults(
  config: MediaBrowserPanelConfig,
  mediaSliderKey: string | undefined,
  isSingleRun: boolean,
  mediaCount?: number,
  layoutMode?: LayoutMode | undefined,
  runsWithMedia?: RunWithHistoryAndMediaWandb[],
  stepMeta?: StepMeta
): MediaConfigWithDefaults {
  const gallerySettings =
    config.gallerySettings ??
    (isSingleRun
      ? {
          axis: 'index' as Axis,
        }
      : {
          axis: 'run' as Axis,
        });

  const gridSettings =
    config.gridSettings ??
    (isSingleRun
      ? {
          xAxis: 'step' as Axis,
          yAxis: 'index' as Axis,
        }
      : {xAxis: 'step' as Axis, yAxis: 'run' as Axis});

  const mode = layoutMode ?? config.mode ?? 'gallery';

  const pixelated = config.pixelated ?? true;

  let actualSize = false;
  let fitToDimension = false;
  if (mode === 'gallery') {
    actualSize = config.actualSize ?? false;
    fitToDimension = config.fitToDimension ?? false;
  }
  const alwaysOriginal = config.alwaysOriginal ?? false;

  const columnCount =
    config.columnCount ?? defaultColumnCount(mediaCount, mode);

  const maxRuns = config.maxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW;

  const maxYAxisCount =
    config.maxYAxisCount ?? PANEL_MEDIA_BROWSER_CONFIG.DEFAULT_MAX_Y_AXIS_COUNT;

  const snapToExistingStep = config.snapToExistingStep ?? true;

  const baseConfig: MediaConfigWithDefaults = {
    ...config,
    actualSize,
    fitToDimension,
    columnCount,
    mode,
    pixelated,
    gallerySettings,
    gridSettings,
    maxRuns,
    maxYAxisCount,
    snapToExistingStep,
    mediaSliderKey,
    alwaysOriginal,
  };

  if (mode !== 'compare' || !runsWithMedia || !stepMeta) {
    return baseConfig;
  }

  const clampedColumnCount = clamp(
    baseConfig.columnCount,
    PANEL_MEDIA_BROWSER_CONFIG.MIN_COMPARE_COL_COUNT,
    PANEL_MEDIA_BROWSER_CONFIG.MAX_COMPARE_COL_COUNT
  );

  const runNames = runsWithMedia.map(r => r.name);
  const mediaKeys = baseConfig.mediaKeys ?? [];
  const compareSettings =
    baseConfig.compareSettings ??
    getCompareConfigDefaults(mediaKeys, runNames, stepMeta, clampedColumnCount);

  return {
    ...baseConfig,
    columnCount: clampedColumnCount,
    compareSettings,
  };
}

export const useOverlays = ({
  allRuns,
  mediaKeys,
}: {
  allRuns: RunWithHistoryAndMediaWandb[];
  mediaKeys: string[] | undefined;
}) => {
  const {allMasks, hasMasks, allBoxes, hasBoxes} = useMemo(() => {
    const masks = classLabelMap(
      allRuns,
      mediaKeys ?? [],
      WANDB_MASK_CLASS_LABEL_KEY
    );
    const boxes = classLabelMap(
      allRuns,
      mediaKeys ?? [],
      WANDB_BBOX_CLASS_LABEL_KEY
    );
    return {
      allMasks: masks,
      hasMasks: Object.keys(masks).length > 0,
      allBoxes: boxes,
      hasBoxes: Object.keys(boxes).length > 0,
    };
  }, [allRuns, mediaKeys]);

  return {
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,
  };
};

export const useAllRuns = ({
  data,
  query,
  pageQuery,
  customRunColors,
}: Pick<
  MediaBrowserPanelProps,
  'data' | 'pageQuery' | 'customRunColors' | 'query'
>) => {
  const {runSetRefs} = usePanelsSharedRefsContext();

  const runSetsInfo = useRunSetsQuery({runSetRefs});
  const idToGroupingMap = useMemo(() => {
    const map: {[key: string]: Grouping | undefined} = {};
    (pageQuery.runSets || []).forEach(runSet => {
      map[runSet.id] = runSet.grouping;
    });
    return map;
  }, [pageQuery.runSets]);

  const runSetEnabledMap = useMemo(() => {
    const map = new Map();
    runSetsInfo.runSets?.forEach(s => {
      if (s.enabled === true) {
        map.set(s.id, true);
      }
    });
    return map;
  }, [runSetsInfo.runSets]);

  const queryMap = useMemo(() => {
    const map = new Map();
    query.runsetQueries.forEach(q => {
      map.set(q.id, q);
    });
    return map;
  }, [query.runsetQueries]);

  const filteredRuns = useMemo(() => {
    return data.histories.data.filter(r => {
      const run = data?.filteredRunsById?.[r.name];
      const runsetID = run?.runsetInfo?.id;
      return runsetID != null && runSetEnabledMap.has(runsetID);
    });
  }, [data.histories.data, data.filteredRunsById, runSetEnabledMap]);

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const allRuns: RunWithHistoryAndMediaWandb[] = useMemo(() => {
    return filteredRuns.map(r => {
      const run = data.filteredRunsById[r.name];
      const runsetID = run.runsetInfo.id;
      const runsetQuery = queryMap.get(runsetID);

      const groupKeys = idToGroupingMap[run.runsetInfo.id];
      const color = groupKeys
        ? runColor({run, groupKeys, customRunColors, colorPaletteOption})
        : undefined;

      if (runsetQuery == null) {
        throw new Error(
          'Inconsistent data from the server, run does not have a matching runset'
        );
      }
      return {
        ...r,
        _wandb: run._wandb,
        entityName: runsetQuery.entityName,
        projectName: runsetQuery.projectName,
        color,
      };
    });
  }, [
    filteredRuns,
    data.filteredRunsById,
    queryMap,
    idToGroupingMap,
    customRunColors,
    colorPaletteOption,
  ]);
  return allRuns;
};

export function useConfigWithDefaults({
  data,
  query,
  config,
  customRunColors,
  pageQuery,
}: Pick<
  MediaBrowserPanelProps,
  'data' | 'query' | 'config' | 'customRunColors' | 'pageQuery'
>): {
  configWithDefaults: MediaConfigWithDefaults;
  allMasks: ClassLabelMap;
  hasMasks: boolean;
  allBoxes: ClassLabelMap;
  hasBoxes: boolean;
} {
  const {mediaKeys, mediaSliderKey: mediaSliderKeyFromConfig} = config;

  const singleRunMode = useSingleRunModeContext();
  const isSingleRun = useMemo(() => {
    return singleRunMode.isSingleRun;
  }, [singleRunMode.isSingleRun]);

  const firstMediaKey = useMemo(() => {
    return mediaKeys?.[0] ?? null;
  }, [mediaKeys]);

  const mediaSliderKey = useMediaSliderKey(mediaSliderKeyFromConfig);

  const allRuns: RunWithHistoryAndMediaWandb[] = useAllRuns({
    data,
    query,
    pageQuery,
    customRunColors,
  });

  const runsWithMedia = useMemo(() => {
    const filtered = allRuns.filter(runHistory => {
      const steps = rowsWithKeysMemo(runHistory, mediaKeys);
      return steps.length > 0;
    });

    return filtered;
  }, [allRuns, mediaKeys]);

  const runStepsMap = useMemo(() => {
    const map = new Map();
    runsWithMedia.forEach(runHistory => {
      map.set(runHistory.name, rowsWithKeysMemo(runHistory, mediaKeys));
    });
    return map;
  }, [runsWithMedia, mediaKeys]);

  const stepMeta = useStepMeta(
    runsWithMedia,
    mediaSliderKey,
    runStepsMap,
    mediaKeys
  );

  const sliderKey = config.mediaSliderKey ?? '_step';
  const currentStep = resolveCurrentStep(config.stepIndex, stepMeta, sliderKey);

  const firstHistory = useMemo(
    () => data.histories.data[0]?.history ?? null,
    [data.histories.data]
  );
  const firstMediaMetadata =
    firstMediaKey != null && firstHistory != null
      ? getMediaMetadata(firstHistory, firstMediaKey, currentStep, sliderKey)
      : null;
  const mediaCount = useMemo(
    () =>
      isSingleRun
        ? ((firstMediaMetadata && firstMediaMetadata.count) ?? 1)
        : data.histories.data.length,
    [isSingleRun, firstMediaMetadata, data.histories.data.length]
  );
  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });
  const firstMaskCount =
    firstMediaKey != null && allMasks[firstMediaKey] != null
      ? Object.keys(allMasks[firstMediaKey]).length
      : 0;
  const layoutMode: LayoutMode = useMemo(() => {
    return config.mode ?? 'gallery';
  }, [config.mode]);

  const configWithDefaults = useMemo(
    () =>
      getConfigWithDefaults(
        config,
        mediaSliderKey,
        isSingleRun,
        mediaCount * (firstMaskCount + 1),
        layoutMode,
        runsWithMedia,
        stepMeta
      ),
    [
      config,
      isSingleRun,
      mediaCount,
      firstMaskCount,
      mediaSliderKey,
      layoutMode,
      runsWithMedia,
      stepMeta,
    ]
  );

  return {
    configWithDefaults,
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,
  };
}
